import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAlert } from "react-alert";
import { APIURL, TZ } from "../../Global";
import "./Clinics/clinics.css";

const channelIcon = {
  sms: "💬",
  whatsapp: "📱",
  email: "✉️"
};

const channelColor = {
  sms: { bg: "#e8f4fd", accent: "#2196f3", badge: "#bbdefb" },
  whatsapp: { bg: "#e8f5e9", accent: "#4caf50", badge: "#c8e6c9" },
  email: { bg: "#fff3e0", accent: "#ff9800", badge: "#ffe0b2" }
};

function StatRow({ label, value, valueStyle }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "6px 0",
        borderBottom: "1px solid #f0f0f0"
      }}
    >
      <span style={{ color: "#6b7280", fontSize: "0.82rem" }}>{label}</span>
      <span style={{ fontWeight: 600, fontSize: "0.88rem", ...valueStyle }}>{value}</span>
    </div>
  );
}

function CreditCard({ channel }) {
  const colors = channelColor[channel.channel_type] || {
    bg: "#f8f9fa",
    accent: "#6c757d",
    badge: "#e9ecef"
  };

  return (
    <div
      style={{
        backgroundColor: "#ffffff",
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        overflow: "hidden",
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        display: "flex",
        flexDirection: "column",
        transition: "box-shadow 0.2s ease"
      }}
      onMouseEnter={e => (e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.12)")}
      onMouseLeave={e => (e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.06)")}
    >
      {/* Card header */}
      <div
        style={{
          backgroundColor: colors.bg,
          padding: "14px 18px",
          display: "flex",
          alignItems: "center",
          gap: 10,
          borderBottom: "1px solid #e5e7eb"
        }}
      >
        <span style={{ fontSize: "1.3rem", lineHeight: 1 }}>{channelIcon[channel.channel_type] || "📦"}</span>
        <span
          style={{
            fontWeight: 700,
            fontSize: "0.95rem",
            textTransform: "capitalize",
            color: colors.accent
          }}
        >
          {channel.channel_type}
        </span>
      </div>

      {/* Total credit highlight */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "12px 18px",
          backgroundColor: colors.badge,
          borderBottom: "1px solid #e5e7eb"
        }}
      >
        <span style={{ fontSize: "0.82rem", color: "#374151", fontWeight: 600 }}>Purchased Credits</span>
        <span
          style={{
            fontSize: "1.25rem",
            fontWeight: 800,
            color: colors.accent
          }}
        >
          {channel.purchased_credits}
        </span>
      </div>

      {/* Stats */}
      <div style={{ padding: "12px 18px", display: "flex", flexDirection: "column", gap: 0 }}>
        <StatRow label="Consumed Credits" value={channel.consumed_credits} />
        <StatRow
          label="Remaining Credits"
          value={channel.remaining_credits}
          valueStyle={{ color: "#16a34a" }}
        />
        <StatRow
          label="Messages Sent"
          value={channel.messages_sent}
        />
        <StatRow
          label="Overused Credits"
          value={channel.overused_messages}
        />
      </div>
    </div>
  );
}

function AllCredits() {
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const alert = useAlert();

  useEffect(() => {
    const fetchCredits = async () => {
      try {
        const tokenString = sessionStorage.getItem("usertoken");
        const token = tokenString ? tokenString.replace(/["]+/g, "") : "";

        const response = await axios.get(`${APIURL}/api/v1/staff/clinic-credit-dashboard/?${TZ}`, {
          headers: { Authorization: "Token " + token },
        });

        if (response.data && Array.isArray(response.data)) {
          const formattedData = response.data.map(clinic => ({
            id: clinic.hospital_id,
            name: clinic.hospital_name,
            credit_detail: Object.keys(clinic.breakdown || {}).map(channel => ({
              id: `${clinic.hospital_id}-${channel}`,
              channel_type: channel,
              purchased_credits: clinic.breakdown[channel].purchased_credits || 0,
              consumed_credits: clinic.breakdown[channel].consumed_credits || 0,
              messages_sent: clinic.breakdown[channel].messages_sent || 0,
              remaining_credits: clinic.breakdown[channel].remaining_credits || 0,
              overused_messages: clinic.breakdown[channel].overused_messages || 0
            }))
          }));
          setClinics(formattedData);
        }
      } catch (err) {
        alert.error("Failed to fetch clinics credit data.");
      } finally {
        setLoading(false);
      }
    };

    fetchCredits();
  }, [alert]);

  const filteredClinics = clinics.filter(clinic =>
    clinic.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div style={{ minHeight: "71vh", padding: "24px 32px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28 }}>
        <h3 className="title-of-page" style={{ marginBottom: 0 }}>
          All Clinics Credits
        </h3>
        <input 
          type="text" 
          placeholder="Search by hospital name..." 
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            padding: "8px 16px",
            borderRadius: "8px",
            border: "1px solid #e2e8f0",
            width: "300px",
            fontSize: "0.95rem"
          }}
        />
      </div>

      {loading ? (
        <div style={{ textAlign: "center", marginTop: "50px" }}>Loading...</div>
      ) : clinics.length === 0 ? (
        <div style={{ textAlign: "center", marginTop: "50px" }}>No credit data found for your search.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 32 }}>
          {filteredClinics.map(clinic => (
            <div
              key={clinic.id}
              style={{
                backgroundColor: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderRadius: 14,
                padding: "20px 24px",
                boxShadow: "0 1px 4px rgba(0,0,0,0.05)"
              }}
            >
              <h5
                style={{
                  fontWeight: 700,
                  fontSize: "1rem",
                  color: "#1e293b",
                  marginBottom: 18,
                  paddingBottom: 12,
                  borderBottom: "2px solid #e2e8f0"
                }}
              >
                🏥 {clinic.name}
              </h5>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(32%, 1fr))",
                  gap: 16
                }}
              >
                {clinic.credit_detail.map(channel => (
                  <CreditCard key={channel.id} channel={channel} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default AllCredits;

